import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Our Approach',
  sub_title: '取り組み',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '多様なユーザーに選ばれ続けるための「3つの柱」',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            「SBI Remit
            アプリ」は、9言語に展開されています。これは、言語・文化・ITリテラシーが全く異なる多様な人々がアプリの使い手となることを意味します。
          </p>
          <p>
            <span className={styles.space_top}>
              拡大し続ける市場の中で競合から選ばれ続けるためには、単なる機能の提供にとどまらず、
              <span className={styles.textbold}>「誰でも、迷わず、直感的に使える」という顧客体験</span>が不可欠でした。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              また、ビジネスニーズの変化に即応するための「開発生産性の向上」、
              <span className={styles.textbold}>そして2025年以降の金融庁推奨基準を考慮した</span>
              「セキュリティ機能の強化」も急務となっていました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              そこで本プロジェクトでは、「使いやすさの改善」「開発生産性の向上」「セキュリティ機能の強化」という3つの軸から、抜本的なリニューアルを推進しました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '1．多様なユーザーを迷わせない。徹底した「ユーザー視点」の体験設計',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            すべてのユーザーが快適に使えるアプリを実現するため、エキスパートレビュー、ペルソナ・シナリオ作成、トンマナの整理、UI設計、ユーザビリティテストを通じて、UX/UIを徹底的に磨き上げました。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'subTitle',
      text: '現状課題の可視化と「安心できる世界観」の構築',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            まずはUX/UIの専門家によるエキスパートレビューでユーザビリティの課題を洗い出しました。そのうえで、
            <span className={styles.textbold}>金融機関としての「信頼感・誠実さ」を基盤</span>にトーン＆マナーを設計。
          </p>
          <p>
            <span className={styles.space_top}>
              加えて、公式キャラクターをエラー時のフィードバックや操作ガイドなどの文脈で適切に組み込み、ユーザーの不安を和らげながら理解を促すインタラクションを設計しました。これにより、
              <span className={styles.textbold}>「親しみやすさ」と「安心感」を両立</span>した世界観を定義しています。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/remit/approach02.webp',
      alt: '現状課題の可視化と「安心できる世界観」の構築',
    },
    {
      type: 'subTitle',
      text: '「直感的な操作」と「自己解決」を促すUI設計',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            UI設計において最も注力したのは、<span className={styles.textbold}>「情報整理」と「導線設計」</span>です。
            ユーザーの最初のハードルとなる会員登録では、入力プロセスを複数のステップに分割し、現在の進捗や残りの工程が把握できるように設計。これにより、入力項目の多さによる心理的負担を軽減し、
            <span className={styles.textbold}>安心して手続きを進められる体験を実現</span>しています。
          </p>
          <p>
            <span className={styles.space_top}>
              入力項目でエラーが発生した際には、単に誤りを示すのではなく、
              <span className={styles.textbold}>
                どの項目をどのように修正すればよいかが直感的に理解できるガイドを表示
              </span>
              しました。エラーの理由と対応方法をその場で明確に伝えることで、ユーザーの迷いや不安を抑え、スムーズな入力完了を支援しています。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              また、<span className={styles.textbold}>ユーザーが久々にアプリを開いても迷わない直感的な導線を構築</span>
              しています。分かりやすいヘルプや問い合わせへのスムーズな誘導により、ユーザーの自己解決率を高め、結果として
              <span className={styles.textbold}>カスタマーサポートの負荷軽減にも貢献</span>しています。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/remit/approach03.webp',
      alt: '「直感的な操作」と「自己解決」を促すUI設計',
    },
    {
      type: 'subTitle',
      text: '「リアルな声」による最終チェック',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            設計したUIが本当に使いやすいかを検証するため、プロトタイプを利用してのフィードバックをもらう、ユーザビリティテストを実施しました。
          </p>
          <p>
            <span className={styles.space_top}>
              アプリのコアユーザーに該当する方を3名お招きして実際に操作していただき、生の声と率直な感想を収集。
              <span className={styles.textbold}>
                デザイナーの視点だけでは気づけない使いにくさを発見し、さらなる改善へとつなげました。
              </span>
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '2．市場のスピードに追随する開発基盤の構築',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            ビジネスを加速させるためには、機能追加や改善のスピードが命です。本プロジェクトでは、2つの技術的アプローチにより、エンジニアの負担軽減と開発スピードの最大化を図りました。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'subTitle',
      text: 'クロスプラットフォーム開発による工数削減',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            これまで分散していたソースコードを統合し、iOSやAndroidなど複数の環境を1つのコードベースで開発可能にする、クロスプラットフォーム開発を行いました。
          </p>
          <p>
            <span className={styles.space_top}>
              これにより、
              <span className={styles.textbold}>改修や新機能実装にかかる工数や影響調査の手間が大幅に削減</span>
              され、デジタルプロダクトとしての継続的な価値向上が容易になりました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'subTitle',
      text: 'WebView型ハイブリッドアプリによる最速の市場投入',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            ネイティブアプリ特有のストア審査によるタイムラグを回避するため、
            Webの技術（HTML、CSS、JavaScript）で作ったコンテンツを、アプリ内のブラウザ機能で表示させる仕組みを用いた、WebView型のハイブリッド構成を採用しています。
          </p>
          <p>
            <span className={styles.space_top}>
              サーバー上のWebサイトデータを更新するだけで最新のデザインやコンテンツをユーザーに届けられるため、
              <span className={styles.textbold}>経営判断や市場の変化をスピーディにアプリへ反映できる仕組みを実現</span>
              しました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/remit/approach04.webp',
      alt: 'WebView型ハイブリッドアプリによる最速の市場投入',
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '3．大切な資産を守り抜く、強固なセキュリティと安定稼働',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            金融アプリである以上、セキュリティとシステムの安定性は決して妥協できない領域です。いつでも、どこからでも安心して利用できるよう、グローバル水準の対策を今回のアプリリニューアルプロジェクトを機に実装しました。
          </p>
        </>,
      ],
    },
    {
      type: 'subTitle',
      text: '金融機関相当の高いレベルのセキュリティ対策で不正利用をブロック',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            大切な資産と個人情報を守るため、フィッシング耐性のある安全な認証設計を採用しました。指紋や顔認証といった生体認証を含む「多要素認証」をログイン時に実装し、
            <span className={styles.textbold}>巧妙化する不正利用のリスクからユーザーを強固に保護</span>します。
          </p>
        </>,
      ],
    },
    {
      type: 'subTitle',
      text: 'グローバルに安定稼働するクラウド配信基盤',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            日本国内はもちろん、海外からのアクセス時でも快適に操作できるよう、クラウドサービスを活用したグローバルな配信基盤を構築しました。
          </p>
          <p>
            <span className={styles.space_top}>
              データの保存から通信までをすべて暗号化し、サイバー攻撃を自動で防ぐ仕組みや、
              <span className={styles.textbold}>常に最新のセキュリティ状態を保つ運用体制を整備</span>しています。
            </span>
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '成果と今後の展望',
    },
    {
      type: 'greyBox',
      namesub: 'SBIレミット株式会社',
      name: '岩本氏',
      texts: [
        <>
          <p>
            本取り組みを通じて、「送金されたお金を守り確実に届ける」という本質に対し、「信頼・安全・落ち着き」をどのように体験として表現するかを深く考える機会となりました。
            UIの細部にこだわることで安心感と使いやすさが向上し、丁寧な設計プロセスにより
            <span className={styles.textbold}>直感的で迷わない体験を実現</span>できました。
          </p>
          <p>
            <span className={styles.space_top}>
              結果として「見やすい・使いやすい」との評価を得られ、提案しやすさや
              <span className={styles.textbold}>サービス全体の信頼向上</span>にもつながっています。
              <br />
              今後もユーザー視点で改善を重ね、使いやすさと信頼性の両立をさらに高めていきます。
            </span>
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
